/**
 * Thin TypeScript bridge to the custom Capacitor plugin `HortonGeofence`.
 *
 * The native implementation lives in
 *   ui-frontend/android/app/src/main/java/io/horton/app/geofence/
 * and is registered via `MainActivity.java` for the Android app.
 *
 * On the web (or if the plugin is missing on the device) every method
 * becomes a safe no-op so the rest of the app continues to work.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'

/**
 * Wire format mirrors what {@code HortonGeofencePlugin.emitEnter} sends:
 * the native side forwards the fence's request id (stringified place id) plus
 * the triggering lat/lng.
 */
export interface GeofenceEntryEvent {
  /** Fence request id — we use `"place:<placeId>"`. */
  id: string
  latitude: number
  longitude: number
  /** Epoch ms recorded on the native side. */
  ts: number
}

/**
 * One fence to register. Matches the Java side's expected payload.
 */
export interface GeofenceSpec {
  id: string
  latitude: number
  longitude: number
  radius_m: number
  /**
   * Dwell delay in milliseconds. Android uses this as
   * {@code Geofence.setLoiteringDelay}, so ENTER fires immediately *and*
   * a DWELL event follows after this delay.
   */
  dwell_ms?: number
}

export interface ReplaceGeofencesOptions {
  fences: GeofenceSpec[]
}

export interface GeofenceDiagnostics {
  pluginVersion: number
  fine: 'granted' | 'denied' | 'prompt'
  background: 'granted' | 'denied' | 'prompt'
  sdkInt: number
  fineGranted: boolean
  backgroundGranted: boolean
  /** The Android APK's numeric versionCode (monotonically increasing). */
  appVersionCode?: number
  /** Full versionName including build suffix, e.g. "1.4.0+2604161510". */
  appVersionName?: string
  /** Clean marketing version without suffix, e.g. "1.4.0". */
  baseVersionName?: string
  /** yyMMddHHmm timestamp captured at Gradle build time. */
  buildStamp?: string
}

export interface HortonGeofencePlugin {
  requestPermissions(): Promise<{
    fine: 'granted' | 'denied' | 'prompt'
    background: 'granted' | 'denied' | 'prompt'
  }>
  hasBackgroundPermission(): Promise<{ granted: boolean }>
  openAppSettings(): Promise<void>
  getDiagnostics(): Promise<GeofenceDiagnostics>
  replaceGeofences(options: ReplaceGeofencesOptions): Promise<{ registered: number }>
  clearAll(): Promise<void>
  addListener(
    eventName: 'enter',
    listener: (event: GeofenceEntryEvent) => void,
  ): Promise<PluginListenerHandle>
}

const _plugin = registerPlugin<HortonGeofencePlugin>('HortonGeofence', {
  web: () => ({
    requestPermissions: async () => ({
      fine: 'denied' as const,
      background: 'denied' as const,
    }),
    hasBackgroundPermission: async () => ({ granted: false }),
    openAppSettings: async () => undefined,
    getDiagnostics: async () => ({
      pluginVersion: 0,
      fine: 'denied' as const,
      background: 'denied' as const,
      sdkInt: 0,
      fineGranted: false,
      backgroundGranted: false,
    }),
    replaceGeofences: async () => ({ registered: 0 }),
    clearAll: async () => undefined,
    addListener: async () => ({
      remove: async () => undefined,
    }) as unknown as PluginListenerHandle,
  }),
})

export const HortonGeofence = _plugin

export function isGeofenceSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

/**
 * Best-effort test that the native plugin is actually registered in the APK.
 * If the Java side is missing (e.g. user is running an older build of the app)
 * `getDiagnostics` will reject with a Capacitor `UNIMPLEMENTED` error.
 *
 * Use the result to distinguish "user denied permission" (actionable via
 * settings) from "the plugin isn't installed" (user needs to update the app).
 */
export async function probeNativePlugin(): Promise<
  | { available: true; diagnostics: GeofenceDiagnostics }
  | { available: false; reason: 'web' | 'unimplemented' | 'error'; message?: string }
> {
  if (!isGeofenceSupported()) {
    return { available: false, reason: 'web' }
  }
  try {
    const diagnostics = await HortonGeofence.getDiagnostics()
    return { available: true, diagnostics }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    const reason = /unimplemented|not implemented/i.test(message)
      ? ('unimplemented' as const)
      : ('error' as const)
    return { available: false, reason, message }
  }
}

/** Canonical request-id format so fence -> place-id translation is lossless. */
export const FENCE_ID_PREFIX = 'place:'

export function fenceIdForPlace(placeId: number): string {
  return `${FENCE_ID_PREFIX}${placeId}`
}

export function placeIdFromFenceId(fenceId: string): number | null {
  if (!fenceId.startsWith(FENCE_ID_PREFIX)) return null
  const parsed = Number(fenceId.slice(FENCE_ID_PREFIX.length))
  return Number.isFinite(parsed) ? parsed : null
}

/** Normalize API/mongo shapes so geofence sync and nudges don't skip string ids. */
export function coercePlaceId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}
